import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { PromptMenuItem } from './VoiceAgentPromptEditor.tsx';

export type PromptInsert = (item: PromptMenuItem) => void;

interface PromptInsertContextValue {
    // the editor that was focused last registers how to insert at its caret; the floating palette calls it
    setActiveInsert: (insert: PromptInsert | null) => void;
    insert: PromptInsert;
    hasTarget: boolean;
}

const PromptInsertContext = createContext<PromptInsertContextValue | null>(null);

// Lets one floating palette serve every prompt editor on the page: editors register themselves on focus, the
// palette inserts into whichever editor was focused last (its caret position is kept by the editor itself).
export const PromptInsertProvider = ({ children }: { children: React.ReactNode }) => {
    const activeInsert = useRef<PromptInsert | null>(null);
    const [hasTarget, setHasTarget] = useState(false);
    const setActiveInsert = useCallback((insert: PromptInsert | null) => {
        activeInsert.current = insert;
        setHasTarget(insert !== null);
    }, []);
    const insert = useCallback<PromptInsert>((item) => activeInsert.current?.(item), []);
    const value = useMemo(() => ({ setActiveInsert, insert, hasTarget }), [setActiveInsert, insert, hasTarget]);
    return <PromptInsertContext.Provider value={value}>{children}</PromptInsertContext.Provider>;
};

export const usePromptInsert = () => useContext(PromptInsertContext);
